import { Home, Shield, AlertTriangle, FileText, Copyright, Scale, Globe, Users, Lock, Eye } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import Footer from "./Footer";
import Navbar from "./Navbar";

const LoadingSpinner = () => {
  return (
    <div style={styles.loadingContainer}>
      <div style={styles.loadingSpinner}>
        <div style={{...styles.spinnerRing, ...styles.ring1}}></div>
        <div style={{...styles.spinnerRing, ...styles.ring2}}></div>
        <div style={{...styles.spinnerRing, ...styles.ring3}}></div>
        <div style={styles.centerText}>Ziegler</div>
      </div>
    </div>
  );
};

const styles = {
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '60vh',
    padding: '2rem',
    textAlign: 'center',
    opacity: 0,
    animation: 'fadeIn 0.4s ease-out forwards'
  },
  loadingSpinner: {
    position: 'relative',
    width: '120px',
    height: '120px',
    margin: '0 auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  spinnerRing: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    border: '4px solid transparent',
    borderTop: '4px solid #099CD6',
    borderRadius: '50%',
    animation: 'spin 1.2s linear infinite'
  },
  ring2: {
    width: '80%',
    height: '80%',
    top: '10%',
    left: '10%',
    borderTop: '4px solid #66b3e0',
    animationDelay: '-0.4s'
  },
  ring3: {
    width: '60%',
    height: '60%',
    top: '20%',
    left: '20%',
    borderTop: '4px solid #99cceb',
    animationDelay: '-0.8s'
  },
  centerText: {
    position: 'relative',
    zIndex: 10,
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#099CD6'
  },
  loadingText: {
    opacity: 0,
    animation: 'fadeIn 0.6s ease-out 0.3s forwards',
    marginTop: '20px',
    fontSize: '18px',
    color: '#666'
  }
};



const TermsAndConditions = () => {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Simulate loading time
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  const sections = [
    {
      id: 'disclaimer',
      title: 'Disclaimer of Warranties, Inaccuracies or Errors',
      icon: <AlertTriangle className="w-6 h-6" />,
      content: (
        <div className="space-y-4">
          <p className="text-white leading-relaxed">
            While we strive for accuracy, we cannot guarantee all information is error-free and disclaim liability for any losses.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-red-50 p-6 rounded-xl">
              <h4 className="font-semibold text-red-900 mb-3">Important Notice</h4>
              <ul className="space-y-2 text-sm text-red-800">
                <li>• Information accuracy not guaranteed</li>
                <li>• No commitment to update content</li>
                <li>• Users must verify suitability</li>
                <li>• Third-party rights not warranted</li>
              </ul>
            </div>
            <div className="bg-amber-50 p-6 rounded-xl">
              <h4 className="font-semibold text-amber-900 mb-3">Your Responsibility</h4>
              <p className="text-sm text-amber-800">
                You must determine the suitability of information for your own purposes before using it. 
                We recommend consulting professionals for critical decisions.
              </p>
            </div>
          </div>
          <div className="bg-gray-900 text-white p-6 rounded-xl">
            <p className="font-semibold text-center">
              THIS WEBSITE AND INFORMATION IS PROVIDED "AS IS", WITHOUT WARRANTY
            </p>
            <p className="text-gray-300 text-sm text-center mt-2">
              Ziegler disclaims any liability for damages arising from its use
            </p>
          </div>
        </div>
      )
    },
    {
      id: 'availability',
      title: 'Availability & Geographic Limitations',
      icon: <Globe className="w-6 h-6" />,
      content: (
        <div className="space-y-4">
          <p className="text-white leading-relaxed">
            Our services are operated from India and may vary by region. Product availability is not guaranteed globally.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg text-center">
              <Globe className="w-8 h-8 mx-auto text-blue-500 mb-2" />
              <h5 className="font-semibold text-blue-900">Base of Operations</h5>
              <p className="text-sm text-blue-700">Website operated from India</p>
            </div>
            <div className="bg-green-50 p-4 rounded-lg text-center">
              <Scale className="w-8 h-8 mx-auto text-green-500 mb-2" />
              <h5 className="font-semibold text-green-900">Regional Variations</h5>
              <p className="text-sm text-green-700">Services may differ by location</p>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg text-center">
              <FileText className="w-8 h-8 mx-auto text-purple-500 mb-2" />
              <h5 className="font-semibold text-purple-900">Product References</h5>
              <p className="text-sm text-purple-700">May not imply local availability</p>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'third-party',
      title: 'Third Party Interaction and Links',
      icon: <Users className="w-6 h-6" />,
      content: (
        <div className="space-y-4">
          <p className="text-white leading-relaxed">
            We are not responsible for third-party content or your interactions with external parties.
          </p>
          <div className="bg-orange-50 border-l-4 border-orange-400 p-6 rounded-r-lg">
            <div className="flex items-start space-x-3">
              <AlertTriangle className="w-6 h-6 text-orange-500 mt-1" />
              <div>
                <h4 className="font-semibold text-orange-900 mb-2">Third-Party Disclaimer</h4>
                <ul className="space-y-1 text-sm text-orange-800">
                  <li>• Transactions are solely between you and third parties</li>
                  <li>• We don't control external website content</li>
                  <li>• Review third-party terms independently</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'intellectual-property',
      title: 'Copyrights & Trademarks',
      icon: <Copyright className="w-6 h-6" />,
      content: (
        <div className="space-y-4">
          <p className="text-white leading-relaxed">
            All materials are protected by intellectual property rights. Proper usage guidelines apply.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-indigo-50 p-6 rounded-xl">
              <Copyright className="w-8 h-8 text-indigo-500 mb-3" />
              <h4 className="font-semibold text-indigo-900 mb-3">Copyright Protection</h4>
              <ul className="space-y-2 text-sm text-indigo-800">
                <li>✓ Personal, non-commercial use allowed</li>
                <li>✗ No modification or distribution</li>
                <li>✗ No commercial exploitation</li>
                <li>✗ Written permission required for other uses</li>
              </ul>
            </div>
            <div className="bg-pink-50 p-6 rounded-xl">
              <Shield className="w-8 h-8 text-pink-500 mb-3" />
              <h4 className="font-semibold text-pink-900 mb-3">Trademark Rights</h4>
              <p className="text-sm text-pink-800">
                All trademarks and logos are owned by Ziegler or affiliates. 
                Usage requires written consent from the respective owners.
              </p>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'user-obligations',
      title: 'User Undertakings & Responsibilities',
      icon: <Users className="w-6 h-6" />,
      content: (
        <div className="space-y-4">
          <p className="text-white leading-relaxed">
            By using our website, you agree to follow these guidelines and restrictions.
          </p>
          <div className="bg-red-50 p-6 rounded-xl">
            <h4 className="font-semibold text-red-900 mb-4 flex items-center">
              <AlertTriangle className="w-5 h-5 mr-2" />
              Prohibited Activities
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <ul className="space-y-2 text-sm text-red-800">
                <li>• Restricting other users' access</li>
                <li>• Transmitting harmful code</li>
                <li>• Infringing intellectual property</li>
                <li>• Using site for illegal purposes</li>
              </ul>
              <ul className="space-y-2 text-sm text-red-800">
                <li>• Commercial use without permission</li>
                <li>• Impersonating others</li>
                <li>• Misrepresenting affiliations</li>
                <li>• Violating any applicable laws</li>
              </ul>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'general-terms',
      title: 'General Terms & Indemnification',
      icon: <Scale className="w-6 h-6" />,
      content: (
        <div className="space-y-4">
          <p className="text-white leading-relaxed">
            Additional terms regarding website monitoring, updates, and user liability.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-gray-50 p-6 rounded-xl">
              <Eye className="w-8 h-8 text-gray-500 mb-3" />
              <h4 className="font-semibold text-gray-900 mb-3">Our Rights</h4>
              <ul className="space-y-2 text-sm text-gray-700">
                <li>• Monitor website usage</li>
                <li>• Cooperate with law enforcement</li>
                <li>• Revise terms without notice</li>
                <li>• Change website format/content</li>
              </ul>
            </div>
            <div className="bg-yellow-50 p-6 rounded-xl">
              <Lock className="w-8 h-8 text-yellow-600 mb-3" />
              <h4 className="font-semibold text-yellow-900 mb-3">Your Obligations</h4>
              <ul className="space-y-2 text-sm text-yellow-800">
                <li>• Responsible for your actions</li>
                <li>• Comply with all laws</li>
                <li>• Indemnify us from claims</li>
                <li>• Follow privacy policy</li>
              </ul>
            </div>
          </div>
          <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-r-lg">
            <p className="text-blue-800 font-medium">
              <Scale className="w-5 h-5 inline mr-2" />
              You agree to indemnify and hold Ziegler harmless from claims arising from your breach of these terms.
            </p>
          </div>
        </div>
      )
    }
  ];

  if (isLoading) {
    return (
      <>
        <Navbar />
        <LoadingSpinner />
        <style jsx>{`
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
          
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
        {/* Hero Section */}
        <div className="text-white py-16" style={{backgroundColor: '#43b6c6'}}>
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center">
              <Scale className="w-16 h-16 mx-auto mb-6 text-slate-200" />
              <h1 className="text-4xl md:text-5xl font-bold mb-4">Terms & Conditions</h1>
              <p className="text-xl text-slate-100 max-w-3xl mx-auto leading-relaxed">
                Please read these terms carefully before using our website. 
                Your continued use constitutes acceptance of these conditions.
              </p>
            </div>
          </div>
        </div>

        {/* Introduction */}
        <div className="max-w-6xl mx-auto px-6 py-12">
          <div className="bg-white rounded-2xl shadow-lg p-8 mb-12">
            <div className="flex items-start space-x-4">
              <FileText className="w-8 h-8 text-blue-500 mt-1" />
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Important Notice</h2>
                <p className="text-gray-600 leading-relaxed text-lg">
                  Visitors to this website are bound by the following terms and conditions. 
                  If you do not agree with any of these terms, please do not use this site. 
                  Your continued use indicates your acceptance of these terms.
                </p>
              </div>
            </div>
          </div>

          {/* Company Reference */}
          <div className="text-white rounded-2xl p-8 mb-8" style={{backgroundColor: '#43b6c6'}}>
            <div className="text-center">
              <Home className="w-12 h-12 mx-auto mb-4 text-blue-200" />
              <h3 className="text-2xl font-bold mb-4">Ziegler Disclaimer</h3>
              <p className="text-blue-100 max-w-2xl mx-auto">
                References to "Ziegler", "Group", "we", or "us" refer to <strong>Ziegler Instruments Limited</strong> and its subsidiaries in general, unless stated otherwise.
              </p>
            </div>
          </div>

          {/* Terms Sections */}
          <div className="space-y-8">
            {sections.map((section, index) => (
              <div key={section.id} className="rounded-2xl shadow-lg overflow-hidden" style={{backgroundColor: '#43b6c6'}}>
                <div className="bg-gradient-to-r from-gray-50 to-slate-50 px-8 py-6 border-b">
                  <div className="flex items-center space-x-4">
                    <div className="bg-slate-600 text-white p-3 rounded-lg">
                      {section.icon}
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">{section.title}</h2>
                      <p className="text-sm text-gray-500">Section {index + 1}</p>
                    </div>
                  </div>
                </div>
                <div className="p-8">
                  {section.content}
                </div>
              </div>
            ))}
          </div>

          {/* Contact Section */}
          <div className="text-white rounded-2xl p-8 mt-12" style={{backgroundColor: '#43b6c6'}}>
            <div className="text-center">
              <FileText className="w-12 h-12 mx-auto mb-4 text-slate-200" />
              <h3 className="text-2xl font-bold mb-4">Questions About These Terms?</h3>
              <p className="text-slate-100 mb-6 max-w-2xl mx-auto">
                For questions or complaints regarding these terms and conditions, please contact our corporate office.
              </p>
              <button className="bg-white text-slate-700 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors">
                Contact Corporate Office
              </button>
            </div>
          </div>

          {/* Last Updated */}
          <div className="text-center mt-8">
            <p className="text-gray-500 text-sm">
              Last updated: May 28, 2025 | Terms may be revised at any time without notice
            </p>
          </div>
        </div>
      </div>
      <Footer />
      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
};

export default TermsAndConditions;